import { useEffect, useState, useCallback, useMemo } from "react";
import { useBusiness } from "@/context/BusinessContext";
import { apiAdmin, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart3, Eye, Clock, Tv as TvIcon, Image as ImageIcon, Video as VideoIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
  BarChart, Bar, CartesianGrid,
} from "recharts";

const RANGES = [
  { label: "24h", days: 1 },
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

function fmtSeconds(s) {
  s = Math.max(0, Math.floor(s));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}

export default function AnalyticsPage() {
  const { activeBusiness } = useBusiness();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!activeBusiness) return;
    setLoading(true);
    try {
      const { data } = await apiAdmin.get(
        `/analytics/summary?business_id=${activeBusiness.id}&days=${days}`
      );
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [activeBusiness, days]);

  useEffect(() => { load(); }, [load]);

  const seriesForChart = useMemo(() => {
    if (!data?.daily_series) return [];
    return data.daily_series.map((d) => ({
      day: d.day.slice(5),  // MM-DD
      impressions: d.impressions,
      minutes: Math.round(d.total_seconds / 60),
    }));
  }, [data]);

  const topMedia = data?.top_media || [];
  const perTv = data?.per_tv || [];

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="font-heading text-3xl font-semibold"
            style={{ color: "#1D1D1F", letterSpacing: "-0.02em" }}
            data-testid="analytics-title"
          >
            Analytics
          </h1>
          <p className="text-sm mt-1" style={{ color: "#6E6E73" }}>
            Impresiones y tiempo en pantalla por contenido — últimos {days} días
          </p>
        </div>
        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: "#D2D2D7" }}>
          {RANGES.map((r) => (
            <button
              key={r.label}
              onClick={() => setDays(r.days)}
              className={`px-4 py-1.5 text-xs font-medium transition-colors ${
                days === r.days ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
              data-testid={`analytics-range-${r.label}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard
          icon={Eye}
          label="Impresiones totales"
          value={data?.totals.impressions ?? 0}
          accent="blue"
          testid="kpi-impressions"
        />
        <KpiCard
          icon={Clock}
          label="Tiempo total en pantalla"
          value={fmtSeconds(data?.totals.total_seconds ?? 0)}
          accent="emerald"
          testid="kpi-screentime"
        />
        <KpiCard
          icon={TvIcon}
          label="TVs activas"
          value={perTv.length}
          accent="purple"
          testid="kpi-tvs"
        />
      </div>

      {/* Time-series chart */}
      <Card title="Actividad diaria" testid="analytics-series-card">
        {seriesForChart.length === 0 ? (
          <EmptyChart message="Aún no hay datos en este rango." />
        ) : (
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <AreaChart data={seriesForChart}>
                <defs>
                  <linearGradient id="g-imp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0A84FF" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#0A84FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#F2F2F4" vertical={false} />
                <XAxis dataKey="day" stroke="#6E6E73" fontSize={11} />
                <YAxis stroke="#6E6E73" fontSize={11} />
                <Tooltip
                  contentStyle={{ borderRadius: 10, border: "1px solid #D2D2D7", fontSize: 12 }}
                  labelStyle={{ color: "#1D1D1F" }}
                />
                <Area type="monotone" dataKey="impressions" stroke="#0A84FF" strokeWidth={2} fill="url(#g-imp)" name="Impresiones" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Top media + per-TV side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Top contenido por tiempo en pantalla" testid="analytics-topmedia-card">
          {topMedia.length === 0 ? (
            <EmptyChart message="Aún no hay contenido reproducido." />
          ) : (
            <ul className="divide-y" style={{ borderColor: "#F2F2F4" }} data-testid="analytics-topmedia-list">
              {topMedia.slice(0, 10).map((m, idx) => (
                <li key={m.media_id} className="flex items-center gap-3 py-2.5">
                  <div className="text-xs font-mono w-6" style={{ color: "#A1A1A6" }}>{String(idx + 1).padStart(2, "0")}</div>
                  <MediaThumb m={m} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" style={{ color: "#1D1D1F" }}>
                      {m.filename}
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: "#6E6E73" }}>
                      {m.impressions} {m.impressions === 1 ? "impresión" : "impresiones"}
                    </div>
                  </div>
                  <div className="text-sm font-medium tabular-nums" style={{ color: "#1D1D1F" }}>
                    {fmtSeconds(m.total_seconds)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Desempeño por TV" testid="analytics-pertv-card">
          {perTv.length === 0 ? (
            <EmptyChart message="Sin TVs activas en el rango." />
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={perTv.map((t) => ({ name: t.tv_name, minutes: Math.round(t.total_seconds / 60), impressions: t.impressions }))}>
                  <CartesianGrid stroke="#F2F2F4" vertical={false} />
                  <XAxis dataKey="name" stroke="#6E6E73" fontSize={10} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis stroke="#6E6E73" fontSize={11} />
                  <Tooltip
                    contentStyle={{ borderRadius: 10, border: "1px solid #D2D2D7", fontSize: 12 }}
                    formatter={(v, n) => n === "minutes" ? [`${v} min`, "Tiempo"] : [v, "Impresiones"]}
                  />
                  <Bar dataKey="minutes" fill="#0A84FF" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      <p className="text-xs text-center pt-2" style={{ color: "#A1A1A6" }}>
        Las impresiones se registran automáticamente cada vez que un Fire Stick reporta un cambio de contenido (heartbeat).
      </p>
    </div>
  );
}

/* --------- Building blocks --------- */

function KpiCard({ icon: Icon, label, value, accent, testid }) {
  const accents = {
    blue: { bg: "#EAF3FF", fg: "#0A84FF" },
    emerald: { bg: "#E6F8EE", fg: "#34C759" },
    purple: { bg: "#F3E8FF", fg: "#AF52DE" },
  };
  const a = accents[accent] || accents.blue;
  return (
    <div
      className="rounded-2xl p-5 bg-white"
      style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ backgroundColor: a.bg }}>
          <Icon className="h-5 w-5" style={{ color: a.fg }} strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest" style={{ color: "#A1A1A6" }}>{label}</div>
          <div className="font-heading text-2xl font-semibold mt-0.5" style={{ color: "#1D1D1F" }}>
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div
      className="rounded-2xl bg-white p-5"
      style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}
      data-testid={testid}
    >
      <div className="text-sm font-semibold mb-3" style={{ color: "#1D1D1F" }}>{title}</div>
      {children}
    </div>
  );
}

function EmptyChart({ message }) {
  return (
    <div className="text-center py-12 text-sm" style={{ color: "#A1A1A6" }}>
      <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" strokeWidth={1.5} />
      {message}
    </div>
  );
}

function MediaThumb({ m }) {
  const isVideo = (m.content_type || "").startsWith("video/");
  if (isVideo) {
    return (
      <div className="h-10 w-14 rounded-md bg-slate-900 grid place-items-center shrink-0">
        <VideoIcon className="h-4 w-4 text-white/60" strokeWidth={1.5} />
      </div>
    );
  }
  if (m.storage_path) {
    return (
      <img
        src={m.storage_path}
        alt=""
        className="h-10 w-14 rounded-md object-cover shrink-0 bg-slate-100"
        loading="lazy"
      />
    );
  }
  return (
    <div className="h-10 w-14 rounded-md bg-slate-100 grid place-items-center shrink-0">
      <ImageIcon className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
    </div>
  );
}
